import { Injectable, computed, signal, type Signal } from '@angular/core';

/** Actions the drive surface registers so the top-nav tree button can drive the explorer's tree. */
export interface DriveTreeController {
  toggleTree(): void;
  /**
   * The tree's SAVED shown state (`!ExplorerStateService.savedTreeCollapsed`), for the
   * `tv` view-context flag; `undefined` until the explorer renders. It differs from the
   * live state only while the tree is in drive's unsaved narrow-load close.
   */
  savedTreeExpanded: Signal<boolean | undefined>;
}

/**
 * Mirror of the drive explorer's tree state for the top-nav tree button (drive-fe
 * ADR-0044). The explorer owns the state — one state at every width: its drawer below
 * 992px, its column (vs the 52px rail) from it — so only the drive surface writes it,
 * via `syncState` from `treeExpandedChange`; the toggle is delegated to the registered
 * controller (`MtmDriveExplorerComponent.toggleTree`). Mirrors `DriveSidebarUiService`.
 *
 * While no drive surface is registered the button falls back to the legacy
 * `explorerCollapsed` UI state, which the non-drive project subsection pages read.
 */
@Injectable({ providedIn: 'root' })
export class DriveTreeUiService {
  private readonly _treeExpanded = signal(false);
  /** The explorer has reported its state at least once since the surface registered. */
  private readonly _synced = signal(false);
  private readonly _controller = signal<DriveTreeController | null>(null);

  /** The live state, for the button's pressed state. */
  readonly treeExpanded = this._treeExpanded.asReadonly();
  /** A drive surface is on screen and owns the tree button. */
  readonly active = computed(() => this._controller() !== null);

  /**
   * The tree's saved shown state for the `tv` view-context flag; `undefined` while there
   * is nothing to report — no drive surface, or the explorer has not reported yet. Saved,
   * not live: a page loaded below 992px starts with the tree closed without saving it
   * (drive's `narrow-load-close.ts`), and mirroring that close would put `tv=0` in a
   * phone-minted link and fold the tree for whoever opens it on a desktop.
   */
  readonly savedExpanded = computed<boolean | undefined>(() =>
    this._synced() ? this._controller()?.savedTreeExpanded() : undefined
  );

  public registerController(controller: DriveTreeController): void {
    this._controller.set(controller);
  }

  /** Identity-checked so a late destroy never clobbers a newer surface's registration. */
  public unregisterController(controller: DriveTreeController): void {
    if (this._controller() === controller) {
      this._controller.set(null);
      this._treeExpanded.set(false);
      this._synced.set(false);
    }
  }

  /** Called only by the drive surface, from the explorer's `treeExpandedChange`. */
  public syncState(treeExpanded: boolean): void {
    this._treeExpanded.set(treeExpanded);
    this._synced.set(true);
  }

  public toggleTree(): void {
    this._controller()?.toggleTree();
  }

  /**
   * Brings the tree to `expanded`, for the `tv` view-context flag. A no-op until the
   * explorer has reported.
   *
   * The explorer flips at once but reports back through an effect, a tick later, so the
   * requested state is recorded here straight away: otherwise a second call before that
   * report would see the old state and toggle the tree back.
   */
  public setTreeExpanded(expanded: boolean): void {
    if (this._synced() && this._treeExpanded() !== expanded) {
      this.toggleTree();
      this._treeExpanded.set(expanded);
    }
  }
}
